import config from 'config';
import type { ActivityFetchType, ActivityQuery, ActivityRaw } from 'redux/modules/activities.types';
import { HOUR_DETAILS_INITIAL_FILTER } from 'redux/modules/hours.types';
import type { BudgetEntity } from 'redux/modules/projects.types';
import type { ReportQueryWithoutLegacyFilters } from 'redux/modules/superreports.types';
import type { HourSummaryRow } from 'redux/modules/superreports-datatable.types';
import type {
  UserPermission,
  UserPermissionResource,
  UserPropertiesRaw,
  UserPropertyRaw,
} from 'redux/modules/user.types';
import type { App, AppAccount, AppEntityImportRequest, AppSubscription } from 'types/apps';
import type { Client, ClientDraft } from 'types/client';
import type { Hour } from 'types/hour';
import type {
  FilterGroupValues,
  LightProject,
  Project,
  ProjectCreateUpdatePayload,
  RateType,
} from 'types/project';
import type { RawCapacity, RawCapacityPayload } from 'types/summaries';
import { TagFilterMode } from 'types/tag';
import type { Team } from 'types/team';
import type { SuggestedEntry } from 'types/timeline';
import type { LightUser, User } from 'types/user';
import type { UsersPayload } from 'types/users';
import type { Merge } from 'types/utility';
import { encodeUrlParams, objectToQueryString } from 'utils/url-params';
import { Delete, Download, Get, Patch, Post, Put } from './core/actions';
import type { ApiResponse } from './core/base';
import { makeRequest, makeRequestAdvanced } from './core/base';

export type UserPermissionsResponse = Array<{
  resource: UserPermissionResource;
  permissions: Array<UserPermission>;
  global_permissions: Array<UserPermission>;
}>;

export interface BulkError {
  index: number;
  messages: Record<string, Array<string> | undefined>;
}

export interface BulkUpdateProjectClientResponse {
  updated_ids: Array<number>;
  errors: Array<{ id: number; message: string }>;
}

export interface BulkResponse {
  created_ids: Array<number>;
  deleted_ids: Array<number>;
  updated_ids: Array<number>;
  errors: {
    create: Array<BulkError>;
    update: Array<BulkError>;
  };
}

const Api = {
  login({
    username,
    password,
    host,
    client_id,
    client_secret,
  }: {
    username: string;
    password: string;
    host: string;
    client_id: string;
    client_secret: string;
  }) {
    const data = {
      username,
      password,
      grant_type: 'password',
      client_id,
      client_secret,
    };
    return this.post(`${host}/1.1/oauth/token`, data, { rawUrl: true, noAuth: true });
  },

  checkLatestAssetRevision(): Promise<{
    frontend: { revision_sha: string };
  }> {
    return this.get('assets/revision.json');
  },

  getActivities(parameters: {
    filter: ActivityFetchType;
    query: ActivityQuery;
  }): Promise<Array<ActivityRaw>> {
    const { limit, offset, order, user_id, project_id } = parameters.query;
    const { filter } = parameters;

    const params = `limit=${limit}&offset=${offset}&order=${order}`;

    if (project_id) {
      return this.get(`projects/${project_id}/activities?${params}`);
    }

    // @todo proper
    if (user_id) {
      return this.get(`users/${user_id}/activities?${params}`);
    }

    return this.get(`activities?filter=${filter}&${params}`);
  },

  getSuggestedEntries(
    date: string,
    options: { exclude_tracker_entries?: null | 'all' | Array<'macOS' | 'Windows' | 'Linux'> } = {}
  ): Promise<Array<SuggestedEntry>> {
    const urlParams: Record<string, string | boolean> = { date, spam: true };
    if (options.exclude_tracker_entries) {
      urlParams.exclude_tracker_entries = Array.isArray(options.exclude_tracker_entries)
        ? options.exclude_tracker_entries.join(',')
        : options.exclude_tracker_entries;
    }
    return this.get(`suggested_entries.json${encodeUrlParams(urlParams)}`);
  },

  /**
   * Delete multiple entry/memory ids
   * Todo: Updated with batch API, ref https://app.clubhouse.io/timelyapp/story/9981/api-batch-delete-multiple-entry-id-s
   */
  deleteEntryIds(ids: Array<number> = []): Promise<Array<unknown>> {
    const promises: Array<Promise<unknown>> = [];
    for (const id of ids) {
      // Synthetic memories have negative IDs, so don't make API requests for these
      if (id < 0) continue;

      const url = `entries/${id}`;
      promises.push(this.delete(url, null, { accountPrefix: false }));
    }
    return Promise.all(promises);
  },

  async markEntryIdsAsSpam(ids: Array<number> = []) {
    // Synthetic memories have negative IDs, so don't make API requests for these
    const realIds = ids.filter(id => id >= 0);
    if (!realIds.length) return;

    return this.post(`suggested_entries/discard.json`, {
      discard_entries: {
        entry_ids: realIds,
      },
    });
  },

  async markEntryIdsAsNotSpam(ids: Array<number> = []) {
    // Synthetic memories have negative IDs, so don't make API requests for these
    const realIds = ids.filter(id => id >= 0);
    if (!realIds.length) return;

    return this.delete(`suggested_entries/discard.json`, {
      discard_entries: {
        entry_ids: realIds,
      },
    });
  },

  predictEntryIdsToProjectId(entryIds: Array<number> = [], project_id: number) {
    return this.put(`suggested_entries.json`, {
      suggested_entries: {
        entries: entryIds.map(entry_id => ({ entry_id, project_id })),
      },
    });
  },

  getRecentProjects(
    userId: number,
    filter: 'active' | 'all' | 'archived',
    { page, perPage }: { page: number; perPage: number }
  ): Promise<Array<LightProject>> {
    const params = {
      user_id: userId,
      per_page: perPage,
      page,
      filter,
    };
    return this.get(`projects/search.json${encodeUrlParams(params)}`);
  },

  async searchForProjects(options: {
    userId: number | null | undefined;
    query?: string;
    rate_type?: RateType;
    filter: FilterGroupValues;
    scope?: 'global';
    rates?: boolean;
    page?: number;
    per_page?: number;
    clientId?: number;
    abortSignal?: AbortSignal;
    labelIds?: Array<number>;
  }): Promise<{ projects: Array<LightProject>; totalCount: number }> {
    const params = {
      user_id: options.userId,
      per_page: options.per_page ?? config.projects.projectsPerPage,
      scope: options.scope,
      rates: options.rates,
      filter: options.filter,
      page: options.page ?? 1,
      q: options.query,
      client_id: options.clientId,
      rate_type: options.rate_type,
      label_ids: options.labelIds,
    };
    const result = await makeRequestAdvanced<Array<LightProject>>(
      `projects/search.json${encodeUrlParams(params)}`,
      'get',
      null,
      { abortSignal: options.abortSignal }
    );
    return {
      projects: result.data,
      totalCount: result.getPaginationHeaders().totalCount,
    };
  },

  getProjects(
    spec: {
      state?: string;
      relation?: string;
      budget?: boolean | undefined;
      labelIds?: Array<number> | undefined;
      clientIds?: Array<number> | undefined;
      sort?: string;
      order?: string;
      query?: string;
      page: number;
      perPage: number;
    },
    opts: {
      includeTotals?: boolean;
      light?: boolean;
    } = {}
  ): Promise<Array<Project>> {
    const options = { includeTotals: false, light: false, ...opts };
    const params = encodeUrlParams({
      state: spec.state,
      relation: spec.relation,
      sort: spec.sort,
      order: spec.order,
      budget: spec.budget,
      label_ids: spec.labelIds,
      client_ids: spec.clientIds,
      q: spec.query,
      limit: spec.perPage,
      offset: (spec.page - 1) * spec.perPage,
      totals: options.includeTotals,
      version: options.light ? 3 : null, // URL hack to get light payloads, will change
    });
    return this.get(`projects.json${params}`);
  },

  async getProjectsWithTotalCount(
    spec: {
      state?: string;
      relation?: string;
      budget?: boolean | undefined;
      labelIds?: Array<number> | undefined;
      clientIds?: Array<number> | undefined;
      sort?: string;
      order?: string;
      query?: string;
      page: number;
      perPage: number;
    },
    opts: {
      includeTotals?: boolean;
    } = {}
  ): Promise<{ projects: Array<Project>; totalCount: number | null }> {
    const options = { includeTotals: false, ...opts };
    const params = encodeUrlParams({
      state: spec.state,
      relation: spec.relation,
      sort: spec.sort,
      order: spec.order,
      budget: spec.budget,
      label_ids: spec.labelIds,
      client_ids: spec.clientIds,
      q: spec.query,
      limit: spec.perPage,
      offset: (spec.page - 1) * spec.perPage,
      totals: options.includeTotals,
    });
    const result = await makeRequestAdvanced<Array<Project>>(`projects.json${params}`, 'get', null);
    const totalCountHeader = result.responseHeaders.get('X-Total-Count');
    return {
      projects: result.data,
      totalCount: totalCountHeader != null ? Number(totalCountHeader) : null,
    };
  },

  getProject(projectId: number): Promise<Project> {
    return this.get(`projects/${projectId}.json`);
  },

  getProjectIds(
    projectIds: Array<number> = [],
    { includeTotals = true }: { includeTotals?: boolean } = {}
  ): Promise<Array<Project>> {
    const params = encodeUrlParams({
      totals: includeTotals,
      filter: 'all',
      project_ids: projectIds,
    });
    return this.get(`projects.json${params}`);
  },

  getProjectSubscriptions(projectId: number) {
    return this.get(`projects/${projectId}/subscription`);
  },

  subscribeToProject(projectId: number) {
    return this.post(`projects/${projectId}/subscribe`);
  },

  unsubscribeFromProject(projectId: number) {
    return this.delete(`projects/${projectId}/subscribe`);
  },

  saveProject(payload: ProjectCreateUpdatePayload): Promise<Project> {
    const {
      update_existing_hours,
      update_unbilled_only,
      update_hour_billable_state,
      update_hours_default_labels,
      ...project
    } = payload;

    // Project API expects nested payload, except for keys related to bulk hour updates:
    const formattedPayload = {
      project,
      update_existing_hours,
      update_unbilled_only,
      update_hour_billable_state,
      update_hours_default_labels,
    };

    if (payload.id) {
      delete formattedPayload.project.id;
      return this.put(`projects/${payload.id}`, formattedPayload);
    }
    // Or create new
    return this.post('projects', formattedPayload);
  },

  // The bulk/projects endpoint returns a simpler error shape than `BulkResponse`
  // above (per-project `{ id, message }` rather than aggregated `{ index, messages }`),
  // so we type it inline here rather than reusing BulkResponse.
  bulkUpdateProjectClient(
    projectIds: number[],
    clientId: number
  ): Promise<BulkUpdateProjectClientResponse> {
    return this.patch('bulk/projects', { project_ids: projectIds, client_id: clientId });
  },

  deleteProject(projectId: number) {
    return this.delete(`projects/${projectId}`);
  },

  getPinnedProjects(): Promise<Array<LightProject>> {
    return this.get(`projects/pinned.json`);
  },

  pinProjectId(projectId: number) {
    return this.post(`projects/${projectId}/pin.json`);
  },

  unpinProjectId(projectId: number) {
    return this.delete(`projects/${projectId}/pin.json`);
  },

  getProjectBudgets(projectId: number): Promise<Array<BudgetEntity>> {
    return this.get(`projects/${projectId}/budget_recurrences`);
  },

  getUsers(
    options: { type?: string } = {},
    { perPage = 1000, page = 1 }: { perPage?: number; page?: number } = {}
  ): Promise<Array<User>> {
    const params: Record<string, unknown> = {
      limit: perPage,
      offset: (page - 1) * perPage,
    };

    if (options.type && options.type !== 'active') {
      params.filter = options.type;
    }

    return this.get(`users.json${encodeUrlParams(params)}`);
  },
  searchUsers(
    query: string,
    { per_page = 50, page = 1 }: { per_page?: number; page?: number } = {}
  ): Promise<Array<LightUser>> {
    const params = encodeUrlParams({
      q: query,
      per_page,
      page,
    });
    return this.get(`users/search${params}`);
  },

  getUser(): Promise<User> {
    const appIsStandalone = !!window.BOOTSTRAPPED_DATA.standaloneClient;
    const url = appIsStandalone ? 'users/current.json' : 'current.json';
    return this.get(url);
  },

  getUserProperties(): Promise<UserPropertiesRaw> {
    return this.get('user_onboarding.json', { accountPrefix: false });
  },

  saveUserProperty(userProperty: UserPropertyRaw) {
    return this.put(
      'user_onboarding.json',
      { user_onboarding_properties: userProperty },
      { accountPrefix: false }
    );
  },

  getUserTeams(userId: number): Promise<Array<Team>> {
    return this.get(`users/${userId}/teams`);
  },

  saveUser(payload: Merge<Partial<User>, { id: number }>) {
    return this.put('identity.json', { user: payload }, { accountPrefix: false });
  },

  inviteOrEditUser(
    payload: UsersPayload,
    options: {
      add_team_users_to_projects: boolean;
      remove_team_users_from_projects: boolean;
    }
  ): Promise<ApiResponse<User>> {
    const {
      update_unbilled_hours_internal_rate,
      update_existing_hours_internal_rate,
      update_existing_hours,
      update_unbilled_only,
      ...user
    } = payload;
    if (payload.id) {
      return makeRequestAdvanced(
        `users/${payload.id}`,
        'put',
        JSON.stringify({
          user,
          update_unbilled_hours_internal_rate,
          update_existing_hours_internal_rate,
          update_existing_hours,
          update_unbilled_only,
          ...options,
        })
      );
    }

    return makeRequestAdvanced(
      'users',
      'post',
      JSON.stringify({
        user,
        update_unbilled_hours_internal_rate,
        update_existing_hours_internal_rate,
        update_existing_hours,
        update_unbilled_only,
        ...options,
      })
    );
  },

  reInviteUser(id: number): Promise<void> {
    return this.put(`users/${id}/resend_invite`);
  },

  getUserById(userId: number): Promise<User> {
    return this.get(`users/${userId}`);
  },

  deleteUser(userId: number) {
    return this.delete(`users/${userId}`);
  },

  async getUserCapacities(): Promise<Array<RawCapacityPayload>> {
    const pageSize = 100;

    let page = 1;
    let results: Array<RawCapacityPayload> = [];

    // Fetch the first page
    do {
      const pageResults = await this.get(
        `users/capacities?limit=${pageSize}&offset=${(page - 1) * pageSize}`
      );

      // Short-circuit the loop if there are no more results
      if (!pageResults.length) break;

      results = results.concat(pageResults);
      page++;
    } while (
      // Continue fetching pages while there might be more results
      results.length > 0 &&
      results.length % pageSize === 0
    );

    return results;
  },

  getUserCapacity(userId: number): Promise<Array<RawCapacity>> {
    return this.get(`users/${userId}/capacities`);
  },

  sendLogHoursRequestNotification(userId: number | Array<number>): Promise<{}> {
    if (Array.isArray(userId)) {
      return this.post('users/timesheet_requests', {
        user_ids: userId.join(','),
      });
    } else {
      return this.post(`users/${userId}/timesheet_requests.json`);
    }
  },

  getClients({
    page = 1,
    perPage = 1_000,
    scope,
  }: {
    page: number;
    perPage?: number;
    scope?: 'global';
  }): Promise<Array<Client>> {
    const params = {
      show: 'all',
      limit: perPage,
      offset: (page - 1) * perPage,
      project_counts: true,
      scope,
    };
    return this.get(`clients.json?${objectToQueryString(params)}`);
  },
  getClient(id: number): Promise<Client> {
    return this.get(`clients/${id}.json?project_counts=true`);
  },

  saveClient(payload: ClientDraft): Promise<Client> {
    const formattedPayload = { client: payload };
    if (payload.id) {
      return this.put(`clients/${payload.id}`, formattedPayload);
    }
    // Or create new
    return this.post('clients', formattedPayload);
  },

  getTeams({ page, perPage }: { page: number; perPage: number }) {
    const queryParams = encodeUrlParams({ page, per_page: perPage });
    return this.get(`teams.json${queryParams}`);
  },

  getTeamById(id: number) {
    return this.get(`teams/${id}`);
  },

  saveTeam(
    payload: Team,
    options: {
      add_users_to_team_projects: boolean;
      delete_users_from_team_projects: boolean;
    } = { add_users_to_team_projects: false, delete_users_from_team_projects: false }
  ): Promise<Team> {
    if (payload.id) {
      return this.put(`teams/${payload.id}`, { team: payload, ...options });
    }
    return this.post('teams', { team: payload });
  },

  deleteTeam(id: number) {
    return this.delete(`teams/${id}`);
  },

  getTimezones() {
    return this.get('timezones.json', { accountPrefix: false });
  },

  getCurrencies() {
    return this.get('currencies.json', { accountPrefix: false });
  },
  getApps(): Promise<Array<App>> {
    return this.get('apps', { accountPrefix: true });
  },
  getAppAccounts(appId: number): Promise<Array<AppAccount>> {
    return this.get(`apps/${appId}/connected`, { accountPrefix: true });
  },

  deleteAppAccount(appId: number, appAccountId: number, isAccountLevelApp: boolean) {
    return this.delete(`apps/${appId}/connected/${appAccountId}`, null, {
      accountPrefix: isAccountLevelApp,
    });
  },

  updateAppAccount<O = unknown>(
    appId: number,
    appAccountId: number,
    data: Partial<AppAccount<O>>
  ): Promise<AppAccount<O>> {
    return this.put(`apps/${appId}/connected/${appAccountId}`, data, { accountPrefix: true });
  },

  // get a list of all clients from external service
  getAppAccountClients(appAccountId: number) {
    return this.get(`apps/${appAccountId}/clients`);
  },
  exportClientToDefaultApp(clientId: number) {
    return this.put(`clients/${clientId}/sync`);
  },
  // create client in external service
  exportClientToAppByConnectionId(connectionId: number, clientId: number) {
    return this.post(`apps/${connectionId}/clients`, {
      client_id: clientId,
    });
  },
  // sync timely client to a specific external id
  syncClientToApp(appAccountId: number, clientId: number, externalId: number) {
    return this.put(`apps/${appAccountId}/clients/${clientId}`, {
      client: { external_id: externalId },
    });
  },

  getAppAccountSubscriptions(accountId: number) {
    return this.get(`apps/${accountId}/objects`, { accountPrefix: false });
  },

  updateAccountSubscriptions(accountId: number, subscriptions: Array<AppSubscription>) {
    // The request URL needs an id number at the end, despite it not being used,
    // see https://app.clubhouse.io/timelyapp/story/4508/calendars-api-for-new-mobile-onboarding
    return this.put(
      `apps/${accountId}/objects/1`,
      {
        app_objects: { objects: subscriptions },
      },
      { accountPrefix: false }
    );
  },

  getUserAccountSettings() {
    return this.get('user_account_settings');
  },

  updateUserAccountSettings(settings: Record<string, string | number | boolean> = {}) {
    const request = {
      settings,
    };

    return this.put('user_account_settings', request);
  },

  getExternalReleaseNotes() {
    return this.get('prod/changelog', {
      host: 'https://82cinm3fhj.execute-api.us-east-1.amazonaws.com',
      accountPrefix: false,
    });
  },

  getUserPermissions(): Promise<UserPermissionsResponse> {
    return this.get('users/current/permissions.json');
  },

  async getTimesheetHours(
    query: ReportQueryWithoutLegacyFilters,
    abortSignal?: AbortSignal,
    customParams: Record<string, string | number | boolean> = {}
  ) {
    const useFilter: any = {
      project_ids: Array.isArray(query.filters.project_ids)
        ? query.filters.project_ids.join(',')
        : query.filters.project_ids,
      user_ids: query.filters.user_ids.join(','),
      team_ids: query.filters.team_ids.join(','),
      label_ids: query.filters.label_ids.join(','),
      state_ids: query.filters.state_ids.join(','),
      client_ids: query.client_ids ? query.client_ids.join(',') : undefined,
      external_link_filter: query.filters.external_link_filter,
      sync_status_filter: query.filters.sync_status_filter,
      include_linked_metadata: query.include_linked_metadata,

      since: query.from,
      until: query.to,
      scope: 'events',
      resource: query.filters.resource,
      limit: query.limit ?? HOUR_DETAILS_INITIAL_FILTER.limit,
      page: Math.max(1, query.page ?? 1),
      select: 'all',
      sort: query.sort ?? HOUR_DETAILS_INITIAL_FILTER.sortBy.field,
      order: query.order ?? HOUR_DETAILS_INITIAL_FILTER.sortBy.direction,
      published: true,

      status: 'all',

      // Default interval if not provided in customParams
      interval: 'daily',

      // Get light projects
      projects_version: 3,

      // Add any custom parameters (interval, interval_value, etc.)
      ...customParams,
    };

    if (query.filters.label_ids.length) {
      useFilter.label_filter = query.filters.label_filter || TagFilterMode.OR;
    }

    const result = await makeRequestAdvanced(
      `reports/filter.json?total_header=true`,
      'POST',
      JSON.stringify(useFilter),
      { abortSignal }
    );

    return {
      data: result.data as Array<Hour<HourSummaryRow | number>>,
      totalCount: result.getPaginationHeaders().totalCount,
    };
  },

  startEntityImport(appAccountId: number, data: AppEntityImportRequest): Promise<AppAccount<any>> {
    return this.put(`apps/${appAccountId}/import`, data, { accountPrefix: true });
  },
  resetAiModel(): Promise<unknown> {
    return this.delete('suggestions/reset');
  },

  get: Get,
  post: Post,
  delete: Delete,
  put: Put,
  patch: Patch,
  download: Download,
  makeRequest,
};

export default Api;
